'use client';

import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type Theme = 'light' | 'dark' | 'system';
export type FontSize = 'sm' | 'md' | 'lg';

export interface Preferences {
  pushEnabled: boolean;
  soundEnabled: boolean;
  emailEnabled: boolean;
  theme: Theme;
  fontSize: FontSize;
  language: string;
  twoFactorEnabled: boolean;
  customTitle: string | null;
  showLastSeen: boolean;
}

interface PrefsState extends Preferences {
  loaded: boolean;
  loading: boolean;
  fetch: () => Promise<void>;
  update: (patch: Partial<Preferences>) => Promise<void>;
  setLocal: (patch: Partial<Preferences>) => void;
}

const DEFAULTS: Preferences = {
  pushEnabled: true,
  soundEnabled: true,
  emailEnabled: false,
  theme: 'light',
  fontSize: 'md',
  language: 'ru',
  twoFactorEnabled: false,
  customTitle: null,
  showLastSeen: true,
};

export const usePreferencesStore = create<PrefsState>()(
  persist(
    (set, get) => ({
      ...DEFAULTS,
      loaded: false,
      loading: false,

      fetch: async () => {
        if (get().loading) return;
        set({ loading: true });
        try {
          const res = await fetch('/api/users/preferences', { credentials: 'include' });
          if (res.ok) {
            const { preferences } = await res.json();
            set({ ...preferences, loaded: true });
          }
        } finally {
          set({ loading: false });
        }
      },

      update: async (patch) => {
        // Optimistic update
        set(patch);
        try {
          const res = await fetch('/api/users/preferences', {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify(patch),
          });
          if (res.ok) {
            const { preferences } = await res.json();
            set(preferences);
          }
        } catch (err) {
          console.error('Preferences update failed', err);
        }
      },

      setLocal: (patch) => set(patch),
    }),
    {
      name: 'next-robot-ee-preferences',
      partialize: (state) => ({
        theme: state.theme,
        fontSize: state.fontSize,
        soundEnabled: state.soundEnabled,
      }),
    }
  )
);
